<template>
  <div>
    <div
      :class="[
        $style.imageBanner,
        spacingStateClass,
        { 'mediabanner-clickable': isClickable },
        { [$style.headerBanner]: isHeaderBanner },
      ]"
      @click="handleImageClick"
    >
      <img
        :alt="mediaItem.altText"
        decoding="async"
        fetchpriority="high"
        loading="eager"
        :src="finalMediaUrl"
        :srcset="mediaItem.imageSrcset"
        :sizes="imageSizes"
        :style="aspectRatio"
      />
      <div :class="[$style.overlaySvgs, 'image-svgs', 'mediabanner-button-overlay']">
        <IconDescription
          v-if="finalShowDescription"
          :aria-label="'Show description for ' + (mediaItem.altText || 'image')"
          tabindex="0"
          @click.stop="handleShowDescription"
        ></IconDescription>
        <IconLink
          v-if="finalShowImageLink && finalLinkUrl"
          :aria-label="mediaItem.caption ? 'Learn more about ' + mediaItem.caption : 'Learn more'"
          tabindex="0"
          @click.stop="linkClicked(finalLinkUrl)"
        ></IconLink>
      </div>
    </div>
    <div
      v-if="showDescriptionOverlay"
      :class="[$style.descriptionOverlay, 'description-overlay']"
      @click="closeDescriptionOverlay"
    >
      <div :class="[$style.overlayContent]" @click.stop>
        <p>{{ mediaItem.caption }}</p>
        <button @click="closeDescriptionOverlay">Close</button>
      </div>
    </div>
    <div class="mediabanner-caption">
      <a :href="mediaItem.captionLink || null">
        <span
          class="captionText"
          :class="[{ column: spacingState == 'Column' }, { withLink: captionLinkExists }]"
          v-html="mediaItem.caption"
        ></span>
        <span v-if="captionLinkExists" class="captionLink"><b>Learn More →</b></span>
      </a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, defineProps, nextTick, useCssModule } from 'vue';
import IconDescription from '@/assets/svgs/media/description.svg?component';
import IconLink from '@/assets/svgs/media/newlink.svg?component';
import { MediaItem } from '@/types/mediaBanner.interface';
import { useAnalytics, useSpacingState } from '@/composables/Common.js';

const $style = useCssModule();

const { ga4Event } = useAnalytics();

const props = withDefaults(
  defineProps<{
    linkUrl?: string;
    mediaItem: MediaItem;
    showDescription?: boolean;
    showLinkButton?: boolean;
    spacingState?: string;
    isHeaderBanner: boolean;
    aspectRatio?: string;
  }>(),
  {
    linkUrl: undefined,
    showDescription: undefined,
    showLinkButton: undefined,
    spacingState: 'Full Screen',
    aspectRatio: 'unset',
  },
);

const showDescriptionOverlay = ref(false);

const aspectRatio = computed(() => {
  return { aspectRatio: (!props.isHeaderBanner && props.aspectRatio) ?? 'unset' };
});
const captionLinkExists = computed(() => {
  return props.mediaItem.captionLink != null && props.mediaItem.captionLink != '';
});
const finalMediaUrl = computed(() => {
  // a file overwrites a url (for now...)
  if (props.mediaItem.imageFile != null && props.mediaItem.imageFile != '') {
    return props.mediaItem.imageFile;
  } else if (props.mediaItem.imageUrl != null && props.mediaItem.imageUrl != '') {
    return props.mediaItem.imageUrl;
  } else {
    return '';
  }
});
const finalShowDescription = computed(() => {
  return props.showDescription !== undefined
    ? props.showDescription
    : props.mediaItem.enableDescription;
});
const finalShowImageLink = computed(() => {
  return props.showLinkButton !== undefined ? props.showLinkButton : props.mediaItem.enableLink;
});
const finalLinkUrl = computed(() => {
  return props.linkUrl !== undefined ? props.linkUrl : props.mediaItem.linksTo;
});
const imageSizes = computed(() => {
  switch (props.spacingState) {
    case 'Column':
      return '(min-width: 768px) 870px, 100vw';
    case 'Inset':
      return '(min-width: 768px) 1280px, 100vw';
    case 'Full Screen':
    case 'None':
    default:
      return '100vw';
  }
});
const isClickable = computed(() => {
  return finalLinkUrl.value != null || finalLinkUrl.value != '';
});

const spacingStateClass = useSpacingState(props, $style);

function closeDescriptionOverlay(): void {
  showDescriptionOverlay.value = false;
}

function handleImageClick(): void {
  if (finalLinkUrl.value) {
    ga4Event({
      click_type: 'image_banner_click',
      component: 'image_banner',
      content_group: 'media',
      link_text: `Image clicked: ${props.mediaItem.altText || 'No Alt Text'}`,
    });
    window.open(finalLinkUrl.value, '_self');
  }
}

function handleShowDescription(): void {
  if (props.mediaItem && props.mediaItem.caption) {
    ga4Event({
      click_type: 'image_banner_show_description',
      component: 'image_banner',
      content_group: 'media',
      link_text: `Show description for: ${mediaItem.altText || 'No Alt Text'}`,
    });
    showDescriptionOverlay.value = true;
    nextTick(() => {
      const overlay = document.querySelector('.description-overlay');

      if (overlay) {
        (overlay as HTMLElement).focus();
      }
    });
  } else {
    alert('No description available.');
  }
}

function linkClicked(url: string): void {
  if (url) {
    ga4Event({
      click_type: 'image_banner_link_click',
      component: 'image_banner',
      content_group: 'media',
      link_text: `Link clicked: ${url}`,
    });
    window.open(url, '_self');
  } else {
    alert('No link URL provided.');
  }
}
</script>

<style lang="scss">
@use '@/styles/banner.module.scss' as *;
</style>

<style lang="scss" module>
.imageBanner {
  position: relative;
  width: 100%;
  height: 100%;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &.fullScreen {
    @include full-bleed-styles;

    & {
      height: 100%;
    }
  }

  &.headerBanner {
    @include full-bleed-styles;

    & {
      height: calc(100dvh - $desktop-header-menu-height);
    }
  }

  .overlaySvgs {
    bottom: 1rem;
    gap: 0.5rem;
    left: 1rem;

    svg {
      height: 44px;
      width: 44px;
    }
  }

  * {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  }
}

@media (min-width: $break-point) {
  .imageBanner {
    &.fullScreen {
      @include full-bleed-styles;

      & {
        height: calc(100vh - $desktop-header-menu-height);
      }
    }
    &.column {
      max-width: $content-max-width;
    }
  }
}
</style>
